import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import { Payer } from "@redotech/redo-model/return-flow";
import { ReturnOptionMethod } from "@redotech/redo-model/return-flow/return-option";
import { returnOptionLabels } from "@redotech/redo-return-ui/flows/return-flow/submit-claim";
import { SubmitWarrantyState } from "@redotech/redo-return-ui/flows/return-flow/submit-warranty";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo, useMemo } from "react";
import { AdjustmentElement, returnOptionMethods } from "./return";

export const SubmitWarrantyDetails = memo(function SubmitWarrantyDetails({
  state,
  setState,
}: StepTypeDetailsProps<SubmitWarrantyState>) {
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );

  const returnOptionMethodsListItems: RedoListItem<
    ReturnOptionMethod["type"]
  >[] = useMemo(
    () =>
      returnOptionMethods.map((method) => ({
        id: method,
        value: method,
        type: "text" as const,
        text: returnOptionLabels(method) || "",
      })),
    [],
  );

  const handleActionChange = useHandler((method: ReturnOptionMethod["type"]) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.method = method;
      }),
    ),
  );
  const handleRequireSendBack = useHandler((value: boolean) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.requireSendBack = value;
      }),
    ),
  );
  const handleSafetyNet = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.labelPayer = value ? Payer.MERCHANT : Payer.CUSTOMER;
      }),
    );
  });
  const handleAdjustmentFlat = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.adjustmentFlat = value;
      }),
    );
  });
  const handleAdjustmentPercent = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.adjustmentPercent = value;
      }),
    );
  });
  const handleDiscountTitle = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.discountTitle = value;
      }),
    );
  });
  const handleChargeCustomerForNewOrderShipping = useHandler(
    (value: boolean) => {
      setState((state) =>
        produce(state, (state) => {
          state.option.chargeCustomerForNewOrderShipping = value;
        }),
      );
    },
  );
  return (
    <>
      <Text fontSize="xs" textColor="secondary">
        Compensation Method
      </Text>
      <RedoSingleSelectDropdownInput
        disabled={!canEditReturnsSettings}
        options={returnOptionMethodsListItems}
        optionSelected={({ item }) => handleActionChange(item.value)}
        selectedItem={returnOptionMethodsListItems.find(
          (item) => item.value === state.option.method,
        )}
        size="xs"
      />
      <AdjustmentElement
        adjustmentFlat={state.option.adjustmentFlat ?? "0"}
        adjustmentPercent={state.option.adjustmentPercent ?? "0"}
        canEditReturnsSettings={canEditReturnsSettings}
        handleAdjustmentFlat={handleAdjustmentFlat}
        handleAdjustmentPercent={handleAdjustmentPercent}
      />
      <RedoToggle
        disabled={!canEditReturnsSettings}
        label="Require customer to send back item?"
        setValue={handleRequireSendBack}
        value={state.option.requireSendBack || false}
      />
      <RedoToggle
        description="Merchant pays for labels not covered by Redo."
        disabled={!canEditReturnsSettings}
        label="Safety net"
        setValue={handleSafetyNet}
        value={state.option.labelPayer === Payer.MERCHANT}
      />
      {state.option.method === ReturnOptionMethod.EXCHANGE && (
        <>
          <RedoTextInput
            description="Title applied to the discount on the draft order."
            label="Discount title"
            setValue={handleDiscountTitle}
            state={canEditReturnsSettings ? "default" : "disabled"}
            value={state.option.discountTitle ?? ""}
          />
          <RedoToggle
            description="Customer pays for shipping on the new exchange order."
            disabled={!canEditReturnsSettings}
            label="Charge customer for new order shipping"
            setValue={handleChargeCustomerForNewOrderShipping}
            value={state.option.chargeCustomerForNewOrderShipping ?? false}
          />
        </>
      )}
    </>
  );
});
